/**
 * ClassRankingBoard — teacher-only ranking of every class by auto-graded
 * accuracy. Complements the existing per-class deep-dive (`ClassStatsBoard`)
 * with a compact "which class needs the most attention right now?" view:
 * every group appears once with its correct/incorrect totals, accuracy
 * badge and the average teacher rating.
 *
 * Backend: GET /api/teacher/classes-ranking
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartBar, ArrowUp, ArrowDown, CheckCircle, XCircle, Star, Spinner } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClassRankingBoard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDir, setSortDir] = useState('worst_first'); // 'worst_first' | 'best_first'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/teacher/classes-ranking`, { withCredentials: true });
        if (!cancelled) setRows(res.data?.classes || []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sorted = React.useMemo(() => {
    const withData = rows.filter(r => r.total > 0);
    const withoutData = rows.filter(r => r.total === 0);
    withData.sort((a, b) => sortDir === 'worst_first'
      ? (a.accuracy - b.accuracy)
      : (b.accuracy - a.accuracy)
    );
    return [...withData, ...withoutData];
  }, [rows, sortDir]);

  const accColor = (acc) => {
    if (acc == null) return 'text-stone-500 bg-stone-50';
    if (acc >= 80) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    if (acc >= 60) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-red-700 bg-red-50 border-red-200';
  };

  return (
    <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 mt-6" data-testid="class-ranking-board">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <ChartBar size={22} weight="fill" className="text-[#AB1C24]" />
          <div>
            <h3 className="font-heading font-semibold text-[#292524] text-lg">
              Klassen-Ranking nach Fehlerquote
            </h3>
            <p className="text-[11px] text-[#78716C] leading-snug mt-0.5">
              Auto-Übungen (Lesen/Hören/Vokabular/Grammatik/Spiele). Nur für Lehrer sichtbar.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSortDir(d => d === 'worst_first' ? 'best_first' : 'worst_first')}
          className="text-xs font-semibold text-[#AB1C24] hover:underline flex items-center gap-1"
          data-testid="ranking-toggle-sort"
        >
          {sortDir === 'worst_first' ? (
            <><ArrowDown size={12} weight="bold" /> Meiste Fehler zuerst</>
          ) : (
            <><ArrowUp size={12} weight="bold" /> Beste Klassen zuerst</>
          )}
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#78716C] py-6 justify-center">
          <Spinner size={16} className="animate-spin" /> Lade Klassen-Ranking…
        </div>
      )}

      {!loading && sorted.length === 0 && (
        <p className="text-sm text-[#A8A29E] italic">Noch keine Klassen vorhanden.</p>
      )}

      {!loading && sorted.length > 0 && (
        <div className="space-y-2">
          {sorted.map((r, idx) => {
            const rankLabel = r.total > 0 ? `#${idx + 1}` : '—';
            return (
              <div
                key={r.group_id}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-stone-200 bg-[#FAF9F6] hover:border-stone-300 transition-colors"
                data-testid={`ranking-row-${r.group_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`text-sm font-bold w-8 text-center ${r.total === 0 ? 'text-stone-400' : 'text-[#292524]'}`}>
                    {rankLabel}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#292524] truncate">{r.name}</p>
                    <p className="text-[11px] text-[#78716C]">
                      {r.level ? `${r.level} · ` : ''}{r.member_count} Schüler
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {r.total > 0 ? (
                    <>
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-[#57534E]">
                        <CheckCircle size={13} weight="fill" className="text-emerald-600" />
                        {r.correct}
                        <XCircle size={13} weight="fill" className="text-red-500 ml-1" />
                        {r.incorrect}
                      </span>
                      {r.avg_manual_score != null && (
                        <span className="hidden md:inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <Star size={11} weight="fill" /> ⌀ {r.avg_manual_score}
                        </span>
                      )}
                      <span
                        className={`text-sm font-bold rounded-full px-3 py-1 border ${accColor(r.accuracy)}`}
                        data-testid={`ranking-accuracy-${r.group_id}`}
                      >
                        {r.accuracy}%
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-[#A8A29E] italic">Noch keine Übungen</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassRankingBoard;
